import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { extractError } from "@/api/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Users, Plus, Building2, ChevronRight, Trash2, Pencil, Briefcase, FileDown, MapPin, ExternalLink, History } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import AuthImage from "@/components/AuthImage";
import AuditHistoryPanel from "@/components/AuditHistoryPanel";
import { useAuth } from "@/contexts/AuthContext";

const HISTORY_ROLES = ["super_admin", "org_admin", "property_manager", "asset_manager", "technical_manager", "auditor"];

const emptyClient = { name: "", contact_name: "", contact_email: "", contact_phone: "", siret: "", notes: "" };
const emptyLE = { client_id: "", name: "", siret: "", address: "", city: "", postal_code: "", country: "France" };

export default function Clients() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canSeeHistory = HISTORY_ROLES.includes(user?.role);
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: () => api.get("/clients").then((r) => r.data) });
  const { data: entities = [] } = useQuery({ queryKey: ["legal-entities"], queryFn: () => api.get("/legal-entities").then((r) => r.data) });
  const { data: assets = [] } = useQuery({ queryKey: ["assets"], queryFn: () => api.get("/assets").then((r) => r.data) });

  const [openClient, setOpenClient] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientForm, setClientForm] = useState(emptyClient);

  const [openLE, setOpenLE] = useState(false);
  const [editingLE, setEditingLE] = useState(null);
  const [leForm, setLeForm] = useState(emptyLE);

  const [expandedClientId, setExpandedClientId] = useState(null);
  const [expandedLEIds, setExpandedLEIds] = useState({});
  const toggleLE = (id) => setExpandedLEIds((prev) => ({ ...prev, [id]: !prev[id] }));
  const [history, setHistory] = useState(null); // { entityType, entityId, title }

  const upsertClient = useMutation({
    mutationFn: (d) => editingClient ? api.patch(`/clients/${editingClient.id}`, d) : api.post("/clients", d),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clients"] });
      toast.success(editingClient ? "Client mis à jour" : "Client créé");
      setOpenClient(false); setEditingClient(null); setClientForm(emptyClient);
    },
    onError: (e) => toast.error(extractError(e, "Erreur")),
  });

  const deleteClient = useMutation({
    mutationFn: (id) => api.delete(`/clients/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["clients"] }); toast.success("Client supprimé"); },
    onError: (e) => toast.error(extractError(e, "Suppression impossible")),
  });

  const upsertLE = useMutation({
    mutationFn: (d) => editingLE ? api.patch(`/legal-entities/${editingLE.id}`, d) : api.post("/legal-entities", d),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["legal-entities"] });
      toast.success(editingLE ? "Entité mise à jour" : "Entité créée");
      setOpenLE(false); setEditingLE(null); setLeForm(emptyLE);
    },
    onError: (e) => toast.error(extractError(e, "Erreur")),
  });

  const deleteLE = useMutation({
    mutationFn: (id) => api.delete(`/legal-entities/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["legal-entities"] }); toast.success("Entité supprimée"); },
    onError: (e) => toast.error(extractError(e, "Suppression impossible")),
  });

  const openCreateClient = () => { setEditingClient(null); setClientForm(emptyClient); setOpenClient(true); };
  const openEditClient = (c) => { setEditingClient(c); setClientForm({ ...emptyClient, ...c }); setOpenClient(true); };
  const openCreateLE = (clientId) => { setEditingLE(null); setLeForm({ ...emptyLE, client_id: clientId }); setOpenLE(true); };
  const openEditLE = (le) => { setEditingLE(le); setLeForm({ ...emptyLE, ...le }); setOpenLE(true); };

  const downloadReport = async (c) => {
    try {
      const res = await api.get(`/clients/${c.id}/report.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      const safe = (c.name || "client").replace(/[^\w-]+/g, "_").slice(0, 40);
      link.download = `dossier_${safe}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Dossier PDF généré");
    } catch (e) {
      toast.error(extractError(e, "Échec de la génération PDF"));
    }
  };

  const downloadLEReport = async (le) => {
    try {
      const res = await api.get(`/legal-entities/${le.id}/report.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      const safe = (le.name || "entite").replace(/[^\w-]+/g, "_").slice(0, 40);
      link.download = `dossier_${safe}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Dossier entité généré");
    } catch (e) {
      toast.error(extractError(e, "Échec de la génération PDF"));
    }
  };

  // Only send whitelisted editable fields — spreading the full client/LE object into the form
  // (for edit mode) also carries id/organization_id/created_at/updated_at, which the backend
  // rejects with "Extra inputs are not permitted" (extra="forbid" on ClientUpdateIn/LegalEntityUpdateIn).
  const cleanClient = (d, allowed) => Object.fromEntries(
    Object.entries(d).filter(([k, v]) => allowed.includes(k) && v !== "" && v !== null)
  );

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Module</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1">Clients &amp; Entités juridiques</h1>
          <p className="text-sm text-slate-500 mt-1.5">{clients.length} client(s) — {entities.length} entité(s) — {assets.length} actif(s) sous gestion</p>
        </div>
        <Button data-testid="new-client-btn" onClick={openCreateClient} className="text-white" style={{ background: "#023E73" }}>
          <Plus size={16} className="mr-1.5" /> Nouveau client
        </Button>
      </div>

      {clients.length === 0 && (
        <Card className="p-10 text-center text-slate-500">
          <Users size={40} className="mx-auto mb-3 text-slate-300" />
          <p className="text-sm">Aucun client. Crée ton premier client pour regrouper tes actifs par donneur d&apos;ordre.</p>
        </Card>
      )}

      <div className="space-y-3">
        {clients.map((c) => {
          const clientLEs = entities.filter((e) => e.client_id === c.id);
          const clientAssetCount = assets.filter((a) => a.client_id === c.id).length;
          const isExpanded = expandedClientId === c.id;
          return (
            <Card key={c.id} data-testid={`client-card-${c.id}`} className="border-slate-200 overflow-hidden">
              <div className="p-4 flex items-center gap-4 hover:bg-slate-50/60 transition cursor-pointer" onClick={() => setExpandedClientId(isExpanded ? null : c.id)}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "#023E7315", color: "#023E73" }}><Briefcase size={20} /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-display font-bold text-slate-900 truncate">{c.name}</h3>
                    {c.siret && <span className="text-[10px] font-mono text-slate-500">SIRET {c.siret}</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
                    <span>{clientLEs.length} entité(s) juridique(s)</span>
                    <span>•</span>
                    <span>{clientAssetCount} actif(s)</span>
                    {c.contact_email && <><span>•</span><span>{c.contact_email}</span></>}
                  </div>
                </div>
                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                  <Button data-testid={`report-client-${c.id}`} variant="ghost" size="sm" onClick={() => downloadReport(c)} title="Générer le dossier PDF"><FileDown size={14} className="text-[#023E73]" /></Button>
                  {canSeeHistory && (
                    <Button data-testid={`history-client-${c.id}`} variant="ghost" size="sm" onClick={() => setHistory({ entityType: "client", entityId: c.id, title: c.name })} title="Historique des modifications"><History size={14} /></Button>
                  )}
                  <Button data-testid={`edit-client-${c.id}`} variant="ghost" size="sm" onClick={() => openEditClient(c)}><Pencil size={14} /></Button>
                  <Button data-testid={`delete-client-${c.id}`} variant="ghost" size="sm" onClick={() => { if (window.confirm(`Supprimer le client « ${c.name} » ?`)) deleteClient.mutate(c.id); }}><Trash2 size={14} className="text-red-500" /></Button>
                  <ChevronRight size={16} className={`text-slate-400 transition ${isExpanded ? "rotate-90" : ""}`} />
                </div>
              </div>
              {isExpanded && (
                <div className="border-t border-slate-100 bg-slate-50/40 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Entités juridiques</div>
                    <Button data-testid={`add-le-${c.id}`} variant="outline" size="sm" onClick={() => openCreateLE(c.id)}><Plus size={12} className="mr-1" /> Ajouter</Button>
                  </div>
                  {clientLEs.length === 0 && <div className="text-xs text-slate-400 italic py-3 text-center">Aucune entité juridique. Ajoute au moins une SCI/SA/SAS pour rattacher les actifs.</div>}
                  <div className="space-y-2">
                    {clientLEs.map((le) => {
                      const leAssets = assets.filter((a) => a.legal_entity_id === le.id);
                      const leExpanded = !!expandedLEIds[le.id];
                      return (
                        <div key={le.id} data-testid={`le-row-${le.id}`} className="bg-white rounded-md border border-slate-200 overflow-hidden">
                          <div className="flex items-center gap-3 p-2.5 hover:bg-slate-50 cursor-pointer" onClick={() => leAssets.length > 0 && toggleLE(le.id)}>
                            <Building2 size={14} className="text-slate-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-slate-900 truncate">{le.name}</div>
                              <div className="text-[11px] text-slate-500 truncate">
                                {le.siret && `SIRET ${le.siret}`}
                                {le.siret && (le.city || le.postal_code) && " • "}
                                {le.postal_code && `${le.postal_code} `}{le.city}
                                {" • "}{leAssets.length} actif(s)
                              </div>
                            </div>
                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                              <Button data-testid={`report-le-${le.id}`} variant="ghost" size="sm" onClick={() => downloadLEReport(le)} title="Générer le dossier PDF de l'entité"><FileDown size={12} className="text-[#023E73]" /></Button>
                              {canSeeHistory && (
                                <Button data-testid={`history-le-${le.id}`} variant="ghost" size="sm" onClick={() => setHistory({ entityType: "legal_entity", entityId: le.id, title: le.name })} title="Historique des modifications"><History size={12} /></Button>
                              )}
                              <Button data-testid={`edit-le-${le.id}`} variant="ghost" size="sm" onClick={() => openEditLE(le)}><Pencil size={12} /></Button>
                              <Button data-testid={`delete-le-${le.id}`} variant="ghost" size="sm" onClick={() => { if (window.confirm(`Supprimer l'entité « ${le.name} » ?`)) deleteLE.mutate(le.id); }}><Trash2 size={12} className="text-red-500" /></Button>
                            </div>
                            {leAssets.length > 0 && <ChevronRight size={14} className={`text-slate-400 transition ${leExpanded ? "rotate-90" : ""}`} />}
                          </div>
                          {leExpanded && leAssets.length > 0 && (
                            <div className="border-t border-slate-100 bg-slate-50/70 px-3 py-2 space-y-1.5">
                              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5 flex items-center gap-1"><MapPin size={10} /> Actifs rattachés</div>
                              {leAssets.map((a) => (
                                <Link key={a.id} to={`/assets/${a.id}`} data-testid={`le-asset-${a.id}`} className="flex items-center gap-3 bg-white rounded-md p-2 border border-slate-100 hover:border-[#023E73] hover:shadow-sm transition group">
                                  <div className="w-9 h-9 rounded-md bg-slate-100 flex-shrink-0 overflow-hidden flex items-center justify-center">
                                    {a.photo_url
                                      ? <AuthImage src={a.photo_url} alt={a.name} className="w-full h-full object-cover" fallback={<Building2 size={12} className="text-slate-300" />} />
                                      : <Building2 size={12} className="text-slate-300" />}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-semibold text-slate-900 truncate">{a.name}</span>
                                      {a.internal_ref && <span className="text-[9px] font-mono text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{a.internal_ref}</span>}
                                    </div>
                                    <div className="text-[10px] text-slate-500 truncate mt-0.5">
                                      {a.postal_code && `${a.postal_code} `}{a.city}
                                      {a.total_surface && <> • {a.total_surface} m²</>}
                                      {a.number_of_buildings > 1 && <> • {a.number_of_buildings} bâtiments</>}
                                    </div>
                                  </div>
                                  <span className={`text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded ${a.risk_score > 70 ? "bg-red-100 text-red-700" : a.risk_score > 50 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>Risk {a.risk_score || 0}</span>
                                  <ExternalLink size={11} className="text-slate-300 group-hover:text-[#023E73]" />
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Client dialog */}
      <Dialog open={openClient} onOpenChange={setOpenClient}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>{editingClient ? "Modifier le client" : "Nouveau client"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nom du client *</Label><Input data-testid="client-name" value={clientForm.name} onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>SIRET</Label><Input value={clientForm.siret || ""} onChange={(e) => setClientForm({ ...clientForm, siret: e.target.value })} /></div>
              <div><Label>Contact principal</Label><Input value={clientForm.contact_name || ""} onChange={(e) => setClientForm({ ...clientForm, contact_name: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email</Label><Input type="email" value={clientForm.contact_email || ""} onChange={(e) => setClientForm({ ...clientForm, contact_email: e.target.value })} /></div>
              <div><Label>Téléphone</Label><Input value={clientForm.contact_phone || ""} onChange={(e) => setClientForm({ ...clientForm, contact_phone: e.target.value })} /></div>
            </div>
            <div><Label>Notes</Label><Textarea rows={2} value={clientForm.notes || ""} onChange={(e) => setClientForm({ ...clientForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button data-testid="client-submit" onClick={() => upsertClient.mutate(cleanClient(clientForm, Object.keys(emptyClient)))} disabled={!clientForm.name} className="text-white" style={{ background: "#023E73" }}>{editingClient ? "Enregistrer" : "Créer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Legal entity dialog */}
      <Dialog open={openLE} onOpenChange={setOpenLE}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>{editingLE ? "Modifier l'entité juridique" : "Nouvelle entité juridique"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nom (SCI, SA, SAS…) *</Label><Input data-testid="le-name" value={leForm.name} onChange={(e) => setLeForm({ ...leForm, name: e.target.value })} /></div>
            <div><Label>SIRET</Label><Input value={leForm.siret || ""} onChange={(e) => setLeForm({ ...leForm, siret: e.target.value })} /></div>
            <div><Label>Adresse</Label><Input value={leForm.address || ""} onChange={(e) => setLeForm({ ...leForm, address: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Code postal</Label><Input value={leForm.postal_code || ""} onChange={(e) => setLeForm({ ...leForm, postal_code: e.target.value })} /></div>
              <div className="col-span-2"><Label>Ville</Label><Input value={leForm.city || ""} onChange={(e) => setLeForm({ ...leForm, city: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="le-submit" onClick={() => upsertLE.mutate(cleanClient(leForm, Object.keys(emptyLE)))} disabled={!leForm.name || !leForm.client_id} className="text-white" style={{ background: "#023E73" }}>{editingLE ? "Enregistrer" : "Créer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {history && (
        <AuditHistoryPanel
          open
          onClose={() => setHistory(null)}
          entityType={history.entityType}
          entityId={history.entityId}
          title={history.title}
        />
      )}
    </div>
  );
}
